import type { QueryClient, UseQueryResult } from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { EventIdPublic } from "@shared/schemas/branded-id.schemas";
import {
  AdminEventDashboardSchema,
  type AdminEventDashboard,
} from "@shared/schemas/admin-event-dashboard.schemas";
import { BaseApiHooksRepository } from "../../base-api-hooks.repository";
import { createDashboardAdminApi } from "./dashboard-admin.api";

/**
 * Its own key on purpose: same URL as the host dashboard but at the admin tier,
 * and one cache key holding two shapes is a first-writer-wins bug.
 */
export const DASHBOARD_ADMIN_QUERY_KEYS = {
  byEvent: (eventIdPublic: EventIdPublic) =>
    ["dashboard-admin", eventIdPublic] as const,
} as const;

interface GetEventDashboardOptions {
  readonly enabled?: boolean;
}

export class DashboardAdminHooksHttpRepositoryImpl extends BaseApiHooksRepository {
  private readonly api;

  constructor(queryClient: QueryClient, apiClient: AxiosInstance) {
    super(queryClient);
    this.api = createDashboardAdminApi(apiClient);
  }

  createHooks() {
    return {
      useGetEventDashboard: (
        eventIdPublic: EventIdPublic,
        options: GetEventDashboardOptions = {},
      ): UseQueryResult<AdminEventDashboard> => {
        const { enabled = true } = options;

        return this.query({
          key: DASHBOARD_ADMIN_QUERY_KEYS.byEvent(eventIdPublic),
          fn: () => this.api.getEventDashboard(eventIdPublic),
          parse: (response) => AdminEventDashboardSchema.parse(response),
          enabled: enabled && EventIdPublic.unwrap(eventIdPublic).length > 0,
          staleTime: 30 * 1000,
          retry: 1,
        });
      },
    };
  }
}
